import { useState } from "react";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface TaskCompleteDialogProps {
  open: boolean;
  taskTitle: string;
  onCancel: () => void;
  onConfirm: (outcome: "positive" | "negative") => Promise<void> | void;
}

export function TaskCompleteDialog({ open, taskTitle, onCancel, onConfirm }: TaskCompleteDialogProps) {
  const [submitting, setSubmitting] = useState<"positive" | "negative" | null>(null);

  const handle = async (outcome: "positive" | "negative") => {
    try {
      setSubmitting(outcome);
      await onConfirm(outcome);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Como foi esta tarefa?</DialogTitle>
          <DialogDescription className="line-clamp-2">{taskTitle}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            variant="outline"
            className="h-24 flex-col gap-2 border-emerald-500/40 text-emerald-800 hover:bg-emerald-500/10 hover:text-emerald-900 dark:text-emerald-200 dark:hover:bg-emerald-500/15 dark:hover:text-emerald-50"
            onClick={() => handle("positive")}
            disabled={submitting !== null}
          >
            {submitting === "positive" ? (
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600 dark:text-emerald-400" />
            ) : (
              <ThumbsUp className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            )}
            <span className="font-medium text-inherit">Positivo</span>
          </Button>
          <Button
            variant="outline"
            className="h-24 flex-col gap-2 border-rose-500/40 text-rose-800 hover:bg-rose-500/10 hover:text-rose-900 dark:text-rose-200 dark:hover:bg-rose-500/15 dark:hover:text-rose-50"
            onClick={() => handle("negative")}
            disabled={submitting !== null}
          >
            {submitting === "negative" ? (
              <Loader2 className="h-6 w-6 animate-spin text-rose-600 dark:text-rose-400" />
            ) : (
              <ThumbsDown className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            )}
            <span className="font-medium text-inherit">Negativo</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
